/**
 * WeeklyObjectivesWidget — Tier 4 P12.
 *
 * Surfaces the active "objective pack" for the week — a curated bundle of
 * 3-7 micro-objectives spanning Memoriz / Games / BalanceTab / Yawmi.
 * Packs are content-defined server-side; the widget shows the pack chosen
 * by the user as "focus pack" (or the first one if none chosen yet).
 *
 * Mobile-first, offline-tolerant:
 *   1. Synchronous render with localStorage cache.
 *   2. Background fetch /v1/objectives for the latest pack catalog + progress.
 *   3. Tapping an objective navigates to the relevant module.
 *   4. Tapping the pack header opens a focus picker (3-pack overflow menu).
 */

import type { Widget, WidgetContext } from '../types';

interface PackObjective {
  id: string;
  verb: string;
  module: string;
  target: number;
  weight: number;
  count: number;
  done: boolean;
  completed_at: string | null;
}

interface Pack {
  id: string;
  theme: 'faith' | 'learning' | 'health' | 'finance' | 'home';
  icon: string;
  accent: string;
  objectives: PackObjective[];
  completed: number;
  total: number;
  is_complete: boolean;
  completed_at: string | null;
  week_start: string;
}

interface PacksPayload {
  week_start: string;
  packs: Pack[];
  focus_pack_id?: string;
}

const LS_CACHE_KEY = 'aweni_objective_packs_v1';
const LS_FOCUS_KEY = 'aweni_objective_focus_pack_v1';

const MODULE_ROUTE: Record<string, string> = {
  memoriz: 'memoriz',
  games: 'games',
  balancetab: 'balancetab',
  yawmi: 'yawmi',
  selecto: 'selecto',
  aweni: 'home',
};

function loadCached(): PacksPayload | null {
  try {
    const raw = localStorage.getItem(LS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PacksPayload;
    if (!parsed?.packs || !Array.isArray(parsed.packs)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCached(payload: PacksPayload): void {
  try {
    localStorage.setItem(LS_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Unwrap `{ok, data}` envelope if present; tolerate raw payloads too. */
function unwrap(json: unknown): PacksPayload | null {
  if (!json || typeof json !== 'object') return null;
  const obj = json as Record<string, unknown>;
  const candidate = (obj.data && typeof obj.data === 'object') ? obj.data : obj;
  const c = candidate as Partial<PacksPayload>;
  if (!Array.isArray(c.packs)) return null;
  return c as PacksPayload;
}

async function fetchPacks(): Promise<PacksPayload | null> {
  try {
    const resp = await fetch('/aweni/api/v1/objectives', {
      cache: 'no-store',
      credentials: 'include',
    });
    if (!resp.ok) return null;
    return unwrap(await resp.json());
  } catch {
    return null;
  }
}

async function setFocusOnServer(packId: string): Promise<PacksPayload | null> {
  try {
    const resp = await fetch('/aweni/api/v1/objectives/focus', {
      method: 'POST',
      cache: 'no-store',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pack_id: packId }),
    });
    if (!resp.ok) return null;
    return unwrap(await resp.json());
  } catch {
    return null;
  }
}

function getLocalFocus(): string | null {
  try {
    return localStorage.getItem(LS_FOCUS_KEY);
  } catch {
    return null;
  }
}

function setLocalFocus(id: string): void {
  try {
    localStorage.setItem(LS_FOCUS_KEY, id);
  } catch {
    /* ignore */
  }
}

function pickFocusPack(payload: PacksPayload): Pack {
  const localFocus = getLocalFocus();
  if (localFocus) {
    const found = payload.packs.find((p) => p.id === localFocus);
    if (found) return found;
  }
  // Default = the pack closest to completion that isn't already done
  const incomplete = payload.packs.filter((p) => !p.is_complete);
  const sorted = (incomplete.length ? incomplete : payload.packs).slice().sort((a, b) => {
    const aRatio = a.total > 0 ? a.completed / a.total : 0;
    const bRatio = b.total > 0 ? b.completed / b.total : 0;
    return bRatio - aRatio;
  });
  return sorted[0];
}

function renderObjectiveRow(obj: PackObjective, ctx: WidgetContext): string {
  const labelKey = `obj_${obj.id}_label`;
  const label = ctx.t(labelKey) || obj.id.replace(/_/g, ' ');
  const ratio = Math.min(1, obj.target > 0 ? obj.count / obj.target : 0);
  const pct = Math.round(ratio * 100);
  const route = MODULE_ROUTE[obj.module] || 'home';

  return `
    <button class="w-full text-left rounded-xl p-3 transition-transform active:scale-[0.99]" style="background:var(--bg-secondary);min-height:44px;" data-obj-route="${route}">
      <div class="flex items-center gap-2">
        <span style="font-size:18px;flex-shrink:0;">${obj.done ? '✅' : '⚪'}</span>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium truncate" style="color:var(--text-primary);text-decoration:${obj.done ? 'line-through' : 'none'};opacity:${obj.done ? '0.6' : '1'};">${label}</div>
          <div class="flex items-center gap-2 mt-1">
            <div class="flex-1 rounded-full overflow-hidden" style="height:4px;background:var(--card-bg);">
              <div class="rounded-full" style="height:100%;width:${pct}%;background:#9C8348;transition:width var(--motion-duration-slow);"></div>
            </div>
            <span class="text-[10px] font-bold tabular-nums" style="color:var(--text-secondary);">${obj.count}/${obj.target}</span>
          </div>
        </div>
      </div>
    </button>
  `;
}

function renderPack(container: HTMLElement, payload: PacksPayload, ctx: WidgetContext): void {
  // Defensive: API or stale cache may yield an envelope w/o `packs`.
  if (!payload || !Array.isArray(payload.packs) || payload.packs.length === 0) {
    container.innerHTML = '';
    return;
  }
  const pack = pickFocusPack(payload);
  const labelKey = `pack_${pack.id}_label`;
  const descKey = `pack_${pack.id}_desc`;
  const packLabel = ctx.t(labelKey) || pack.id.replace(/_/g, ' ');
  const packDesc = ctx.t(descKey) || '';
  const headerProgress = `${pack.completed}/${pack.total}`;
  const completionPct = pack.total > 0 ? Math.round((pack.completed / pack.total) * 100) : 0;
  const completedBanner = pack.is_complete
    ? `<div class="text-[11px] font-bold px-2 py-1 rounded-full" style="background:#16A34A1A;color:#16A34A;">✓ ${ctx.t('pack_complete') || 'Pack complete'}</div>`
    : '';

  // Up to 5 visible, scrollable below; mobile-first stays under 320px tall.
  const objs = pack.objectives.slice(0, 5).map((o) => renderObjectiveRow(o, ctx)).join('');
  const overflow = pack.objectives.length > 5
    ? `<div class="text-[11px] text-center mt-2" style="color:var(--text-secondary);">+ ${pack.objectives.length - 5} ${ctx.t('pack_more') || 'more'}</div>`
    : '';
  const otherPacks = payload.packs
    .filter((p) => p.id !== pack.id)
    .slice(0, 3)
    .map((p) => `
      <button class="text-[11px] px-2.5 py-1 rounded-full" style="background:var(--bg-secondary);border:1px solid var(--border);color:var(--text-secondary);" data-pack-switch="${p.id}">
        ${p.icon} ${ctx.t(`pack_${p.id}_label`) || p.id}
      </button>
    `)
    .join('');

  container.innerHTML = `
    <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="flex items-center justify-center rounded-xl" style="background:${pack.accent}1A;width:36px;height:36px;flex-shrink:0;">
            <span style="font-size:20px;">${pack.icon}</span>
          </div>
          <div class="min-w-0">
            <div class="text-sm font-semibold" style="color:var(--text-primary);">${packLabel}</div>
            ${packDesc ? `<div class="text-[11px] truncate" style="color:var(--text-secondary);">${packDesc}</div>` : ''}
          </div>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          ${completedBanner}
          <span class="text-xs font-bold tabular-nums" style="color:${pack.accent};">${headerProgress}</span>
        </div>
      </div>
      <div class="rounded-full overflow-hidden mb-3" style="height:6px;background:var(--bg-secondary);">
        <div class="rounded-full" style="height:100%;width:${completionPct}%;background:${pack.accent};transition:width var(--motion-duration-slow);"></div>
      </div>
      <div class="space-y-2">${objs}</div>
      ${overflow}
      ${otherPacks ? `<div class="flex flex-wrap gap-1.5 mt-3 pt-3" style="border-top:1px solid var(--border);">${otherPacks}</div>` : ''}
    </div>
  `;

  container.querySelectorAll<HTMLElement>('[data-obj-route]').forEach((el) => {
    el.addEventListener('click', () => {
      const route = el.dataset.objRoute;
      if (route) ctx.navigate(route);
    });
  });

  container.querySelectorAll<HTMLElement>('[data-pack-switch]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const id = el.dataset.packSwitch;
      if (!id) return;
      setLocalFocus(id);
      // Re-render immediately with the new focus
      renderPack(container, payload, ctx);
      // Persist server-side (best-effort, fail-silent)
      void setFocusOnServer(id);
    });
  });
}

function renderGuest(container: HTMLElement, ctx: WidgetContext): void {
  container.innerHTML = `
    <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
      <div class="flex items-center gap-3">
        <span style="font-size:24px;opacity:0.5;">🎯</span>
        <div class="flex-1">
          <div class="text-sm font-semibold" style="color:var(--text-primary);">${ctx.t('pack_guest_title') || 'Weekly family goals'}</div>
          <div class="text-xs mt-0.5" style="color:var(--text-secondary);">${ctx.t('pack_guest_desc') || 'Sign in to unlock cross-app objective packs'}</div>
        </div>
        <button class="text-xs font-medium px-3 py-1.5 rounded-full" style="background:var(--accent);color:var(--accent-text);" data-nav-login>
          ${ctx.t('action_sign_in') || 'Sign in'}
        </button>
      </div>
    </div>
  `;
  container.querySelector<HTMLElement>('[data-nav-login]')?.addEventListener('click', () => {
    ctx.navigate('login');
  });
}

export const WeeklyObjectivesWidget: Widget = {
  id: 'weekly-objectives',
  label: {
    en: 'Weekly Objectives',
    fr: 'Objectifs de la semaine',
    ar: 'أهداف الأسبوع',
    es: 'Objetivos de la semana',
    de: 'Wochenziele',
  },
  icon: '🎯',
  defaultVisible: true,
  defaultOrder: 7,
  defaultSize: 'expanded',

  render(container: HTMLElement, _size: 'compact' | 'expanded', ctx: WidgetContext): void {
    if (ctx.isGuest) {
      renderGuest(container, ctx);
      return;
    }

    const cached = loadCached();
    if (cached) {
      renderPack(container, cached, ctx);
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <div class="text-sm font-semibold" style="color:var(--text-primary);">${ctx.t('pack_loading_title') || 'Loading weekly goals...'}</div>
          <div class="text-xs mt-1" style="color:var(--text-secondary);">${ctx.t('pack_loading_desc') || 'Picking the right pack for your week.'}</div>
        </div>
      `;
    }

    queueMicrotask(async () => {
      const fresh = await fetchPacks();
      if (fresh) {
        saveCached(fresh);
        if (container.isConnected) renderPack(container, fresh, ctx);
      }
    });
  },
};
